import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useUser, useClerk } from '@clerk/react';

const AuthContext = createContext();

const DEV_MODE = process.env.REACT_APP_DEV_MODE === 'true';

export const AuthProvider = ({ children }) => {
  const { user: clerkUser, isLoaded: isUserLoaded, isSignedIn } = useUser();
  const { signOut: clerkSignOut } = useClerk();
  const [accountRole, setAccountRole] = useState(null);
  const [roleLoading, setRoleLoading] = useState(true);
  const [accountError, setAccountError] = useState(null);

  const [devRole, setDevRole] = useState(() => {
    return localStorage.getItem('devRole') || null;
  });

  useEffect(() => {
    if (DEV_MODE || !isSignedIn) {
      setRoleLoading(false);
      setAccountRole(null);
      setAccountError(null);
      return;
    }
    let active = true;
    setRoleLoading(true);
    (async () => {
      try {
        const response = await fetch('/api/auth/user', {
          credentials: 'include',
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.error || 'Could not verify your approved account.');
        if (active) { setAccountRole(result.role); setAccountError(null); }
      } catch (error) {
        if (active) { setAccountRole(null); setAccountError(error.message); }
      } finally {
        if (active) setRoleLoading(false);
      }
    })();
    return () => { active = false; };
  }, [isSignedIn, clerkUser?.id]);

  // Memoize so the object reference only changes when real user data changes,
  // not on every Clerk session-poll re-render (fixes ~40-45s modal auto-close).
  const user = useMemo(() => {
    if (DEV_MODE && devRole) {
      return {
        id: `dev-user-${devRole}`,
        email: `${devRole}@dev.viysr.com`,
        first_name: 'Dev',
        last_name: devRole.charAt(0).toUpperCase() + devRole.slice(1),
        name: 'Dev ' + devRole.charAt(0).toUpperCase() + devRole.slice(1),
        role: devRole,
        profile_image_url: null,
      };
    }
    if (isSignedIn && clerkUser) {
      return {
        id: clerkUser.id,
        email: clerkUser.emailAddresses?.[0]?.emailAddress || '',
        first_name: clerkUser.firstName || '',
        last_name: clerkUser.lastName || '',
        name: clerkUser.fullName || clerkUser.firstName || '',
        role: accountRole,
        profile_image_url: clerkUser.imageUrl || null,
      };
    }
    return null;
  }, [
    devRole,
    isSignedIn,
    clerkUser?.id,
    clerkUser?.emailAddresses?.[0]?.emailAddress,
    clerkUser?.firstName,
    clerkUser?.lastName,
    clerkUser?.fullName,
    clerkUser?.publicMetadata?.role,
    clerkUser?.imageUrl,
    accountRole,
  ]);

  const isLoading = DEV_MODE ? false : !isUserLoaded || (!!isSignedIn && roleLoading);

  const signIn = () => {
    if (DEV_MODE) {
      setDevRole('advisor');
      localStorage.setItem('devRole', 'advisor');
    } else {
      window.location.href = '/get-started';
    }
  };

  const signOut = async () => {
    if (DEV_MODE) {
      setDevRole(null);
      localStorage.removeItem('devRole');
    } else {
      await clerkSignOut({ redirectUrl: '/' });
    }
  };

  const setRole = (role) => {
    if (DEV_MODE) {
      setDevRole(role);
      localStorage.setItem('devRole', role);
    }
  };

  // Browser API requests use Clerk's same-origin session cookie.
  const getAuthHeader = useCallback(() => ({}), []);

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    error: accountError,
    signIn,
    signOut,
    setRole,
    refetch: () => {},
    getAuthHeader,
    isDev: DEV_MODE,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const DevAuthProvider = AuthProvider;

export default AuthContext;
